import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../services/api';
import Pagination from '../../components/common/Pagination';
import { useTranslation } from '../../context/LanguageContext';

/* 요금제 배지 색 (platform-badge) */
const LICENSE_BADGE = { ENTERPRISE: 'pending', STANDARD: 'info', BASIC: 'active', TRIAL: '' };

/* 사용량 — 최대값(계약 한도)이 없으면 막대 없이 현재 / — */
function Usage({ current, max }) {
    if (!max) return <span>{current} / <span className="muted">—</span></span>;
    const ratio = current / max;
    return (
        <div className="platform-usage">
            <span>{current} / {max}</span>
            <span className="platform-usage-track">
                <span className={`platform-usage-bar ${ratio > 0.8 ? 'high' : ''}`} style={{ width: `${Math.min(ratio * 100, 100)}%` }} />
            </span>
        </div>
    );
}

function LicenseManagementPage() {
    const { t } = useTranslation();
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        fetchLicenses();
    }, [page, size]);

    const fetchLicenses = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            const response = await fetch(`${getApiBaseUrl()}/platform/companies?page=${page}&size=${size}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                const licenseData = (data.content || []).map(company => ({
                    id: company.id,
                    companyName: company.companyName,
                    licenseType: company.subscriptionPlan || null,
                    maxUsers: company.maxUsers || null,
                    currentUsers: company.userCount || 0,
                    maxEquipments: company.maxEquipment || null,
                    currentEquipments: company.equipmentCount || 0,
                    startDate: company.contractStartDate,
                    endDate: company.contractEndDate,
                    status: company.subscriptionStatus || (company.isActive ? 'ACTIVE' : 'INACTIVE')
                }));
                setLicenses(licenseData);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
            }
        } catch (error) {
            console.error('라이선스 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('ko-KR');
    };

    const getLicenseTypeLabel = (type) => {
        switch (type) {
            case 'ENTERPRISE': return t('pages.platform.licenseManagement.type.enterprise');
            case 'STANDARD': return t('pages.platform.licenseManagement.type.standard');
            case 'BASIC': return t('pages.platform.licenseManagement.type.basic');
            case 'TRIAL': return t('pages.platform.licenseManagement.type.trial');
            default: return type || '—';
        }
    };


    const getDaysRemaining = (endDate) => {
        if (!endDate) return 999;
        const end = new Date(endDate);
        const today = new Date();
        const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    return (
        <div className="platform-page">

            {/* 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">license</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{totalElements}</div>
                        <div className="platform-stat-label">{t('pages.platform.licenseManagement.stats.total')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {licenses.filter(l => l.status === 'ACTIVE').length}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.licenseManagement.stats.active')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">warning</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {licenses.filter(l => getDaysRemaining(l.endDate) <= 30 && getDaysRemaining(l.endDate) > 0).length}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.licenseManagement.stats.expiring')}</div>
                    </div>
                </div>
            </div>

            {/* 라이선스 목록 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">{t('pages.platform.licenseManagement.listTitle')}</h3>
                </div>
                {loading ? (
                    <div className="platform-empty">
                        <div className="platform-empty-text">{t('pages.platform.licenseManagement.loading')}</div>
                    </div>
                ) : licenses.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">license</span>
                        <div className="platform-empty-text">{t('pages.platform.licenseManagement.empty')}</div>
                    </div>
                ) : (
                    <>
                        <div className="platform-table-scroll">
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>{t('pages.platform.licenseManagement.col.company')}</th>
                                    <th>{t('pages.platform.licenseManagement.col.type')}</th>
                                    <th>{t('pages.platform.licenseManagement.col.users')}</th>
                                    <th>{t('pages.platform.licenseManagement.col.equipments')}</th>
                                    <th>{t('pages.platform.licenseManagement.col.startDate')}</th>
                                    <th>{t('pages.platform.licenseManagement.col.endDate')}</th>
                                    <th>{t('pages.platform.licenseManagement.col.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {licenses.map((license) => {
                                    const daysRemaining = getDaysRemaining(license.endDate);
                                    return (
                                        <tr key={license.id}>
                                            <td><strong>{license.companyName}</strong></td>
                                            <td>
                                                {license.licenseType ? (
                                                    <span className={`platform-badge ${LICENSE_BADGE[license.licenseType] || ''}`}>
                                                        {getLicenseTypeLabel(license.licenseType)}
                                                    </span>
                                                ) : <span className="muted">—</span>}
                                            </td>
                                            <td>
                                                <Usage current={license.currentUsers} max={license.maxUsers} />
                                            </td>
                                            <td>
                                                <Usage current={license.currentEquipments} max={license.maxEquipments} />
                                            </td>
                                            <td>{formatDate(license.startDate)}</td>
                                            <td>
                                                {formatDate(license.endDate)}
                                                {daysRemaining <= 30 && daysRemaining > 0 && (
                                                    <div className="platform-days-left">
                                                        {t('pages.platform.licenseManagement.daysRemaining', { n: daysRemaining })}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`platform-badge ${license.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                                    {license.status === 'ACTIVE'
                                                        ? t('pages.platform.licenseManagement.statusActive')
                                                        : t('pages.platform.licenseManagement.statusExpired')}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default LicenseManagementPage;
